import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { UploadCsvModalType } from "../../../constants/utlis";
import { all_routes } from "../../../feature-module/router/all_routes";
import { TbUpload } from "react-icons/tb";
import { uploadLogHourCsv } from "../../data/redux/logHour/_logHourAction";
import UploadSuccessIcon from "../../../icons/UploadSuccessIcon";
import CsvFileIcon from "../../../icons/CsvFileIcon";

interface UploadCsvModalProps {
  show: boolean;
  onHide: () => void;
  type: UploadCsvModalType;
  uuid?: string | null;
  data?: any
}

const UploadCsvModal: React.FC<UploadCsvModalProps> = ({ show, onHide, type, uuid, data }) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes: any = all_routes;

  // State for managing upload stage, progress, and selected file
  const [uploadStage, setUploadStage] = useState<"initial" | "uploading" | "complete">("initial");
  const [progress, setProgress] = useState<number>(0);
  const [selectedCSV, setSelectedCSV] = useState<File | null>(null);

  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleCSVChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === "text/csv" || file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
      setSelectedCSV(file);
      setUploadStage("uploading");
      setProgress(0);
    } else {
      toast.error("Please select a valid CSV or XLSX file");
    }
  };

  // Simulate progress bar (replace with actual API progress if available)
  useEffect(() => {
    if (uploadStage === "uploading") {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            setUploadStage("complete");
            clearInterval(interval);
            return 100;
          }
          return prev + 10; // Increment by 10% every 500ms (adjust as needed)
        });
      }, 500);
      return () => clearInterval(interval);
    }
  }, [uploadStage]);

  // Handle API call on Upload button click
  const handleUploadCSV = async () => {
    if (!selectedCSV || uploadStage !== "complete") return;

    try {
      switch (type) {
        case "uploadLogHourCsv":
          if (!selectedCSV) {
            toast.error("No file selected for uploading");
            return;
          }

          const formData = new FormData();
          formData.append("file", selectedCSV);
          await dispatch(uploadLogHourCsv(formData));

          handleClearUpload();
          onHide();
          break;

        default:
          toast.error("Invalid upload type");
          break;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.error || "Upload failed");
      } else {
        toast.error("An error occurred during upload");
      }
      console.error(error);
    }
  };

  // Clear upload
  const handleClearUpload = () => {
    setSelectedCSV(null);
    setUploadStage("initial");
    setProgress(0);
  };

  // Handle div click to trigger file input
  const handleDivClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    handleClearUpload();
    onHide();
  };

  // Render modal content based on stage
  const renderModalContent = () => {
    switch (uploadStage) {
      case "initial":
        return (
          <div
            className="border rounded p-4 text-center mb-3"
            style={{ cursor: "pointer" }}
            onClick={handleDivClick}
          >
            <div className="mb-3">
              <TbUpload className="modal-upload-button" />
            </div>
            <label
              className="d-block fw-semibold"
            >
              Tap to upload CSV
            </label>
            <small className="text-muted d-block">
              Browse and choose the file you want to upload from your computer
            </small>
            <input
              type="file"
              id="csvUpload"
              accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="d-none"
              onChange={handleCSVChange}
              ref={fileInputRef}
            />
          </div>
        );

      case "uploading":
        return (
          <div className="border rounded p-4 text-center mb-3">
            <div className="mb-3">
              <CsvFileIcon />
              <p className="mt-3 mb-3">{progress}%</p>
              <div className="progress">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>
            <label
              htmlFor="csvUpload"
              className="d-block fw-semibold"
              style={{ cursor: "pointer" }}
            >
              Uploading Document
            </label>
            {selectedCSV && (
              <div className="mt-2 text-success fw-semibold">
                Selected: {selectedCSV.name}
              </div>
            )}
          </div>
        );

      case "complete":
        return (
          <div className="border rounded p-4 text-center mb-3">
            <div className="mb-2">
              <UploadSuccessIcon />
            </div>
            <label
              htmlFor="csvUpload"
              className="d-block fw-semibold"
              style={{ cursor: "pointer" }}
            >
              Uploading Complete
            </label>
            {selectedCSV && (
              <div className="mt-2 text-success fw-semibold">
                Selected: {selectedCSV.name}
              </div>
            )}
            <div className="clear-upload-area mt-4">
              <span onClick={handleClearUpload} style={{ cursor: "pointer" }}>
                Clear Upload
              </span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };



  return (
    <Modal
      className="Upload-modal"
      show={show}
      onHide={handleModalClose}
      centered
    >
      <div className="p-4 mb-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">
            Upload CSV <small className="text-muted">(Optional)</small>
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleModalClose}
          ></button>
        </div>

        {renderModalContent()}

        <button
          type="button"
          className="btn primaryButton btn-primary d-inline-flex justify-content-center align-items-center btn-block w-100"
          onClick={handleUploadCSV}
          disabled={uploadStage !== "complete"}
        >
          Upload
        </button>
      </div>
    </Modal>
  );
};

export default UploadCsvModal;